/**
 * PAGE D'INVITATION PUBLIQUE — /parrainage/:code
 * Un ami arrive via le lien personnel d'un membre. Page premium autonome :
 * réserve un cours d'essai payant (crédite le parrain) + accès boutique.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { toast } from "sonner";
import { CircleNotch, ArrowRight, CheckCircle, WarningCircle, Storefront, Barbell } from "@phosphor-icons/react";

const pub = axios.create({ baseURL: API });
const LOGO_WHITE = "/logos/master_text_sm.webp";
const ENSO = "/logos/kaizen_system/generated/kaizen_system_icon_only_enso_K.png";
const MASTER = "/logos/institut_impact/iiem_master_wordmark_disciplines_transparent.png";
const EMBLEMS = [{ label: "Kickboxing" }, { label: "Muay Thaï" }, { label: "Jiu-jitsu brésilien" }, { label: "Jiu-jitsu Can-Ryu" }, { label: "PowerKick Her" }];

export default function ParrainagePublic() {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | invalid | done
  const [form, setForm] = useState({ friend_name: "", friend_email: "", friend_phone: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    pub.get(`/referral/info/${code}`)
      .then((r) => { setInfo(r.data); setState("ready"); })
      .catch(() => setState("invalid"));
  }, [code]);

  async function submit(e) {
    e.preventDefault();
    if (!form.friend_name.trim() || !form.friend_email.trim()) return toast.error("Nom et courriel requis");
    setBusy(true);
    try {
      const r = await pub.post("/referral/convert", { code, ...form });
      setState("done");
      toast.success(r.data.message);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Erreur — réessaie");
    }
    setBusy(false);
  }

  if (state === "loading") {
    return <div className="kz-body min-h-screen flex items-center justify-center bg-[#0A0A0A]"><CircleNotch size={36} className="text-[#D90429] animate-spin" /></div>;
  }
  if (state === "invalid") {
    return (
      <div className="kz-body min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white p-6" data-testid="parrainage-invalid">
        <div className="text-center">
          <WarningCircle size={48} weight="fill" className="text-white/50 mx-auto" />
          <p className="kz-display text-2xl mt-4">Lien d'invitation invalide</p>
          <Link to="/boutique-accueil" className="inline-block mt-6 bg-white text-black px-7 py-3 font-bold tracking-wider hover:bg-[#D90429] hover:text-white transition-colors">Visiter la boutique</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="kz-body min-h-screen bg-[#0A0A0A] text-white grid lg:grid-cols-2" data-testid="parrainage-public">
      {/* Visuel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden bg-[#0A0A0A]" data-testid="parrainage-visual">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(217,4,41,0.22),transparent_60%),radial-gradient(ellipse_at_bottom_right,rgba(201,169,97,0.14),transparent_55%)]" />
        <img src={ENSO} alt="" className="absolute -left-16 -top-16 w-[360px] opacity-[0.06]" />
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center">
          <img src={MASTER} alt="Institut Impact Élite Martial — Kickboxing, Muay Thaï, Jiu-jitsu brésilien, Can-Ryu, PowerKick Her" className="w-full max-w-xl object-contain drop-shadow-[0_0_40px_rgba(217,4,41,0.3)] kz-fade-up" data-testid="parrainage-master-logo" />
          <p className="kz-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mt-8 text-center">{EMBLEMS.map((e) => e.label).join(" · ")}</p>
        </div>
        <div className="relative z-10">
          <img src={LOGO_WHITE} alt="Institut Impact Élite Martial" className="h-14 object-contain" />
          <p className="kz-mono text-[11px] tracking-[0.26em] uppercase text-white/50 mt-6">Institut Impact Élite Martial · Val-d'Or</p>
        </div>
      </div>

      {/* Contenu */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md kz-fade-up">
          {state === "done" ? (
            <div className="text-center" data-testid="parrainage-done">
              <CheckCircle size={56} weight="fill" className="text-[#D90429] mx-auto" />
              <h1 className="kz-display text-3xl mt-5">C'est réservé !</h1>
              <p className="kz-body text-white/60 mt-3">Ton cours d'essai (19,99 $) t'attend. On te contacte très vite pour fixer ta première séance. À bientôt sur le tatami ! 🥋</p>
              <Link to="/boutique-accueil" data-testid="done-boutique-btn" className="inline-flex items-center gap-2 mt-8 border border-white/15 hover:border-[#D90429] px-6 py-3 transition-colors">
                <Storefront size={18} /> Découvrir la boutique
              </Link>
            </div>
          ) : (
            <>
              <span className="kz-mono text-[10px] tracking-[0.26em] uppercase text-[#D90429]">Invitation personnelle</span>
              <h1 className="kz-display text-4xl sm:text-5xl font-medium mt-3 leading-[0.95]">
                {info.referrer_first_name} t'invite à<br /><span className="text-[#D90429]">t'entraîner</span> avec nous.
              </h1>
              <p className="kz-body text-white/60 mt-4">Réserve ton <b className="text-white">cours d'essai à 19,99 $</b> à l'Institut Impact Élite Martial — Muay Thaï, Kickboxing, BJJ, Can-Ryu & PowerKick Her.</p>

              <form onSubmit={submit} className="mt-8 space-y-5" data-testid="parrainage-form">
                {[["friend_name", "Ton nom", "text", "Prénom Nom"], ["friend_email", "Ton courriel", "email", "toi@courriel.com"], ["friend_phone", "Ton téléphone (optionnel)", "tel", "819-000-0000"]].map(([k, label, type, ph]) => (
                  <div key={k}>
                    <label className="kz-mono text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-2">{label}</label>
                    <input type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph}
                      data-testid={`parrainage-${k}`} required={k !== "friend_phone"}
                      className="w-full bg-transparent border-b border-white/20 focus:border-[#D90429] outline-none text-white placeholder-white/25 py-2" />
                  </div>
                ))}
                <button type="submit" disabled={busy} data-testid="parrainage-submit"
                  className="w-full bg-[#D90429] text-white py-4 font-bold tracking-wider hover:bg-white hover:text-black transition-colors flex items-center justify-center gap-3 disabled:opacity-50">
                  {busy ? <CircleNotch size={18} className="animate-spin" /> : <><Barbell size={18} weight="fill" /> Réserver mon cours d'essai <ArrowRight size={18} weight="bold" /></>}
                </button>
              </form>
              <p className="kz-body text-xs text-white/35 mt-5">Aucun engagement. Tu seras contacté pour planifier ta séance d'essai.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
